import { Injectable } from '@angular/core';
import { RepositoryService } from '../baseServices/repository.service';
import {
  ClockInAuxillaryDto,
  ClockInResponseDto,
  CancelMealBreakAuxillaryDto,
  CancelMealBreakResponseDto,
  CancelRestBreakAuxillaryDto,
  StartMealBreakAuxillaryDto,
  StartMealBreakResponseDto,
  EndMealBreakAuxillaryDto,
  EndMealBreakResponseDto,
  StartNewAssignmentAuxillaryDto,
  StartNewAssignmentResponseDto,
  ClockOutAuxillaryDto,
  ClockOutResponseDto,
  CreateClosedWorkingTimeAuxillaryDto,
  CreateClosedWorkingTimeResponseDto,
  CreateClosedMealBreakTimeAuxillaryDto,
  CreateClosedMealBreakTimeResponseDto,
  UpdateTimeClocksAuxillaryDto,
  UpdateAssignmentAuxillaryDto,
  UpdateAssignmentResponseDto,
  SubmitTimeAuxillaryDto,
  VerifyTimeAuxillaryDto,
  ApproveTimeAuxillaryDto,
  ReturnTimeAuxillaryDto,
  CreateNotableActivityAuxillaryDto,
  CreateNotableActivityResponseDto,
  CreateFlexDocActivityAuxillaryDto,
  CreateFlexDocActivityResponseDto,
  UpdateNotableActivityAuxillaryDto,
  DeleteTimeAuxillaryDto,
  DeleteNotableActivityAuxillaryDto
} from '../dtos/sync-action-service-dtos';
import { ActivityLog } from '../entities/ActivityLog';
import { StoreName } from '../entities/StoreName';
import { TimeRecord } from '../entities/timeRecord';
import { DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { DbTransaction } from '../models/db/db-transaction';
import { ActivityService } from './activity.service';
import { ActivityLogService } from './activityLog.service';
import { TimeRecordService } from './timeRecord.service';
import { CreateClosedRestBreakTimeAuxillaryDto } from '../dtos/sync-action-service-dtos/auxillary-dtos/create-closed-rest-break-time-auxillary-data-dto';
import { CreateClosedRestBreakTimeResponseDto } from '../dtos/sync-action-service-dtos/response-dtos/create-closed-rest-break-time-response-dto';
import { StartRestBreakAuxillaryDto } from '../dtos/sync-action-service-dtos/auxillary-dtos/start-rest-break-auxillary-dto';
import { StartRestBreakResponseDto } from '../dtos/sync-action-service-dtos/response-dtos/start-rest-break-response-dto';
import { UpdateOdometerReadingAuxillaryDto } from '../dtos/sync-action-service-dtos/auxillary-dtos/update-odometer-reading-auxillary-dto';
import { UpdateOdometerReadingResponseDto } from '../dtos/sync-action-service-dtos/response-dtos/update-odometer-reading-response-dto';
import { OdometerReadingService } from './odometer-reading.service';
import { DeleteOdometerReadingAuxillaryDto } from '../dtos/sync-action-service-dtos/auxillary-dtos/delete-odometer-reading-auxillary-dto';
import { CreateOdometerReadingAuxillaryDto } from '../dtos/sync-action-service-dtos/auxillary-dtos/create-odometer-reading-auxillary-dto';
import { CreateOdometerReadingResponseDto } from '../dtos/sync-action-service-dtos/response-dtos/create-odometer-reading-response-dto';
import { OdometerReadingMediaService } from './odometer-reading-media.service';
import { SavedSyncAction } from '../models/sync-action/sync-action-interface';
import { SyncActionTypeId } from '../constant/sync-action-constants';
import { ServiceInjections } from '../models/sync-action/sync-action-types-interface';
import { DependantSyncActionException } from '../utils/exceptions';

@Injectable({
  providedIn: 'root',
})
export class TimeSyncService {
  constructor(
    private timeRecordService: TimeRecordService,
    private activityLogService: ActivityLogService,
    private activityService: ActivityService,
    private odometerReadingService: OdometerReadingService,
    private repositoryService: RepositoryService,
    private odometerReadingMediaService: OdometerReadingMediaService
  ) { }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction, additionalStoreNames?: StoreName[], mode?: 'readwrite' | 'readonly'): Promise<DbTransaction> {
    if (existingTransaction) {
      return existingTransaction;
    } else {
      const storeNames = [
        StoreName.Activity,
        StoreName.ActivityLog,
        StoreName.TimeRecord,
        StoreName.StatusLookup,
        StoreName.TimeRecordStatus,
        StoreName.EntityType,
        StoreName.OdometerReading,
        StoreName.OdometerReadingMedia,
        ...(additionalStoreNames || []),
      ];
      return await this.repositoryService.startTransaction(Array.from(new Set(storeNames)), mode ?? 'readwrite');
    }
  }

  async onClockInDoBefore(action: SavedSyncAction<SyncActionTypeId.ClockIn>) {
    // Upload media if odometer reading media exists
    if (action.auxillaryData.OdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.OdometerReadingMediaRecordId);
      action.payload.OdometerStartPhotoBlobName = fileName;
      
      // Update the odometer reading record with the blob name
      if (fileName && action.auxillaryData.OdometerReadingRecordId) {
        const transaction = await this.startTransactionIfNeeded();
        await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.OdometerReadingRecordId, 'StartPhotoBlobName', fileName, transaction);
      }
    }
  }

  async onClockInSuccess(auxillaryData: ClockInAuxillaryDto, response: ClockInResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, response.TimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, response.ClockInActivityId, transaction),
      ...(response.OdometerReadingId ? [this.odometerReadingService.updateOdometerReadingWithId(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, response.OdometerReadingId, transaction)] : []),
    ]);
  }

  async onClockInFailure(auxillaryData: ClockInAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, true, null, transaction),
      ...(auxillaryData.OdometerReadingRecordId ? [this.odometerReadingService.hardDeleteOdometerReading(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, true, null, transaction)] : []),
    ]);
  }

  async onStartMealBreakDoBefore(serviceInjections: ServiceInjections, action: SavedSyncAction<SyncActionTypeId.StartMealBreak>) {
    if (!action.params.openTimeRecordId) {
      const timeRecord = await serviceInjections.timeRecordService.getTimeRecordByRecordId(action.auxillaryData.ExistingTimeRecord.RecordId, null, true);
      if (!timeRecord?.Id) {
        throw new DependantSyncActionException(timeRecord);
      }
      action.params.openTimeRecordId = timeRecord.Id;
    }
    // Upload media if odometer reading media exists
    if (action.auxillaryData.StartOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.StartOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerStartPhotoBlobName = fileName;
        
        // Update the new odometer reading record with the start blob name
        if (action.auxillaryData.OdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.OdometerReadingRecordId, 'StartPhotoBlobName', fileName, transaction);
        }
      }
    }
    if (action.auxillaryData.EndOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.EndOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerEndPhotoBlobName = fileName;
        
        // Update the closed odometer reading record with the end blob name
        if (action.auxillaryData.ClosedOdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.ClosedOdometerReadingRecordId, 'EndPhotoBlobName', fileName, transaction);
        }
      }
    }
  }

  async onStartMealBreakSuccess(auxillaryData: StartMealBreakAuxillaryDto, response: StartMealBreakResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, response.TimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.BreakActivityRecordId, response.BreakActivityId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, response.ClockOutActivityId, transaction),
      ...(response.OdometerReadingId ? [this.odometerReadingService.updateOdometerReadingWithId(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, response.OdometerReadingId, transaction)] : []),
    ]);
  }

  async onStartMealBreakFailure(auxillaryData: StartMealBreakAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.BreakActivityRecordId, true, null, transaction),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, true, null, transaction),
      ...(auxillaryData.ExistingOdometerReading ? [this.odometerReadingService.updateOdometerReading(DataChangeEmitWho.Sync, auxillaryData.ExistingOdometerReading, true, null, transaction)] : []),
      ...(auxillaryData.OdometerReadingRecordId ? [this.odometerReadingService.hardDeleteOdometerReading(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, true, null, transaction)] : []),
    ]);
  }
  async onEndMealBreakDoBefore(serviceInjections: ServiceInjections, action: SavedSyncAction<SyncActionTypeId.EndMealBreak>) {
    if (!action.params.openTimeRecordId) {
      const timeRecord = await serviceInjections.timeRecordService.getTimeRecordByRecordId(action.auxillaryData.ExistingTimeRecord.RecordId, null, true);
      if (!timeRecord?.Id) {
        throw new DependantSyncActionException(timeRecord);
      }
      action.params.openTimeRecordId = timeRecord.Id;
    }
    // Upload media if odometer reading media exists
    if (action.auxillaryData.StartOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.StartOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerStartPhotoBlobName = fileName;
        
        // Update the new odometer reading record with the start blob name
        if (action.auxillaryData.OdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.OdometerReadingRecordId, 'StartPhotoBlobName', fileName, transaction);
        }
      }
    }
    if (action.auxillaryData.EndOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.EndOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerEndPhotoBlobName = fileName;
        
        // Update the closed odometer reading record with the end blob name
        if (action.auxillaryData.ClosedOdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.ClosedOdometerReadingRecordId, 'EndPhotoBlobName', fileName, transaction);
        }
      }
    }
  }

  async onEndMealBreakSuccess(auxillaryData: EndMealBreakAuxillaryDto, response: EndMealBreakResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, response.TimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, response.ClockInActivityId, transaction),
      ...(response.OdometerReadingId ? [this.odometerReadingService.updateOdometerReadingWithId(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, response.OdometerReadingId, transaction)] : []),
    ]);
  }

  async onEndMealBreakFailure(auxillaryData: EndMealBreakAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, true, null, transaction),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      ...(auxillaryData.ExistingOdometerReading ? [this.odometerReadingService.updateOdometerReading(DataChangeEmitWho.Sync, auxillaryData.ExistingOdometerReading, true, null, transaction)] : []),
      ...(auxillaryData.OdometerReadingRecordId ? [this.odometerReadingService.hardDeleteOdometerReading(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, true, null, transaction)] : []),
    ]);
  }

  async onCancelMealBreakSuccess(auxillaryData: CancelMealBreakAuxillaryDto, response: CancelMealBreakResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, response.TimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, response.ClockInActivityId, transaction),
    ]);
  }

  async onCancelMealBreakFailure(auxillaryData: CancelMealBreakAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, true, null, transaction),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      ...auxillaryData.ExistingActivities.map(activity => this.activityService.updateActivity(DataChangeEmitWho.Sync, activity, true, null, transaction)),
    ]);
  }

  async onCancelRestBreakSuccess(_: CancelRestBreakAuxillaryDto) {
    return;
  }

  async onCancelRestBreakFailure(auxillaryData: CancelRestBreakAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingBreakTimeRecord, true, null, transaction),
      ...auxillaryData.ExistingBreakActivities.map(activity => this.activityService.updateActivity(DataChangeEmitWho.Sync, activity, true, null, transaction)),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingWorkingTimeRecord, true, null, transaction),
      ...auxillaryData.ExistingWorkingClockActivities.map(activity => this.activityService.updateActivity(DataChangeEmitWho.Sync, activity, true, null, transaction)),
    ]);
  }

  async onStartRestBreakSuccess(auxillaryData: StartRestBreakAuxillaryDto, response: StartRestBreakResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.BreakTimeRecordRecordId, response.BreakTimeRecordId, transaction),
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.WorkingTimeRecordRecordId, response.WorkingTimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.BreakActivityRecordId, response.BreakActivityId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, response.ClockOutActivityId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, response.ClockInActivityId, transaction),
    ]);
  }

  async onStartRestBreakFailure(auxillaryData: StartRestBreakAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.WorkingTimeRecordRecordId, true, null, transaction),
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.BreakTimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.BreakActivityRecordId, true, null, transaction),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, true, null, transaction),
    ]);
  }

  async onStartNewAssignmentDoBefore(serviceInjections: ServiceInjections, action: SavedSyncAction<SyncActionTypeId.StartNewAssignment>) {
    if (!action.params.openTimeRecordId) {
      const timeRecord = await serviceInjections.timeRecordService.getTimeRecordByRecordId(action.auxillaryData.ExistingTimeRecord.RecordId, null, true);
      if (!timeRecord?.Id) {
        throw new DependantSyncActionException(timeRecord);
      }
      action.params.openTimeRecordId = timeRecord.Id;
    }
    // Upload media if odometer reading media exists
    if (action.auxillaryData.StartOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.StartOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerStartPhotoBlobName = fileName;
        
        // Update the new odometer reading record with the start blob name
        if (action.auxillaryData.OdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.OdometerReadingRecordId, 'StartPhotoBlobName', fileName, transaction);
        }
      }
    }
    if (action.auxillaryData.EndOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.EndOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerEndPhotoBlobName = fileName;
        
        // Update the closed odometer reading record with the end blob name
        if (action.auxillaryData.ClosedOdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.ClosedOdometerReadingRecordId, 'EndPhotoBlobName', fileName, transaction);
        }
      }
    }
  }

  async onStartNewAssignmentSuccess(auxillaryData: StartNewAssignmentAuxillaryDto, response: StartNewAssignmentResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, response.TimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, response.ClockInActivityId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, response.ClockOutActivityId, transaction),
      ...(response.OdometerReadingId ? [this.odometerReadingService.updateOdometerReadingWithId(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, response.OdometerReadingId, transaction)] : []),
    ]);
  }

  async onStartNewAssignmentFailure(auxillaryData: StartNewAssignmentAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, true, null, transaction),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, true, null, transaction),
      ...(auxillaryData.ExistingOdometerReading ? [this.odometerReadingService.updateOdometerReading(DataChangeEmitWho.Sync, auxillaryData.ExistingOdometerReading, true, null, transaction)] : []),
      ...(auxillaryData.OdometerReadingRecordId ? [this.odometerReadingService.hardDeleteOdometerReading(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, true, null, transaction)] : []),
    ]);
  }

  async onClockOutDoBefore(serviceInjections: ServiceInjections, action: SavedSyncAction<SyncActionTypeId.ClockOut>) {
    if (!action.params.openTimeRecordId) {
      const timeRecord = await serviceInjections.timeRecordService.getTimeRecordByRecordId(action.auxillaryData.ExistingTimeRecord.RecordId, null, true);
      if (!timeRecord?.Id) {
        throw new DependantSyncActionException(timeRecord);
      }
      action.params.openTimeRecordId = timeRecord.Id;
    }
    // Upload media if odometer reading media exists
    if (action.auxillaryData.OdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.OdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerEndPhotoBlobName = fileName;
        
        // Update the odometer reading record with the end blob name
        if (action.auxillaryData.OdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.OdometerReadingRecordId, 'EndPhotoBlobName', fileName, transaction);
        }
      }
    }
  }

  async onClockOutSuccess(auxillaryData: ClockOutAuxillaryDto, response: ClockOutResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, response.ClockOutActivityId, transaction),
    ]);
  }

  async onClockOutFailure(auxillaryData: ClockOutAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, true, null, transaction),
      ...(auxillaryData.ExistingOdometerReading ? [this.odometerReadingService.updateOdometerReading(DataChangeEmitWho.Sync, auxillaryData.ExistingOdometerReading, true, null, transaction)] : []),
    ]);
  }

  async onCreateClosedWorkingTimeSuccess(auxillaryData: CreateClosedWorkingTimeAuxillaryDto, response: CreateClosedWorkingTimeResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, response.TimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, response.ClockInActivityId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, response.ClockOutActivityId, transaction),
    ]);
  }

  async onCreateClosedWorkingTimeFailure(auxillaryData: CreateClosedWorkingTimeAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockInActivityRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ClockOutActivityRecordId, true, null, transaction),
    ]);
  }

  async onCreateClosedMealBreakTimeSuccess(auxillaryData: CreateClosedMealBreakTimeAuxillaryDto, response: CreateClosedMealBreakTimeResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, response.TimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.BreakActivityRecordId, response.BreakActivityId, transaction),
    ]);
  }

  async onCreateClosedMealBreakTimeFailure(auxillaryData: CreateClosedMealBreakTimeAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.BreakActivityRecordId, true, null, transaction),
    ]);
  }

  async onCreateClosedRestBreakTimeSuccess(auxillaryData: CreateClosedRestBreakTimeAuxillaryDto, response: CreateClosedRestBreakTimeResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.timeRecordService.updateTimeRecordWithId(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, response.TimeRecordId, transaction),
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.BreakActivityRecordId, response.BreakActivityId, transaction),
    ]);
  }

  async onCreateClosedRestBreakTimeFailure(auxillaryData: CreateClosedRestBreakTimeAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.timeRecordService.hardDeleteTimeRecord(DataChangeEmitWho.Sync, auxillaryData.TimeRecordRecordId, true, null, transaction),
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.BreakActivityRecordId, true, null, transaction),
    ]);
  }

  async onUpdateTimeClocksFailure(auxillaryData: UpdateTimeClocksAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.activityLogService.updateActivityLog(DataChangeEmitWho.Sync, auxillaryData.ExistingActivityLog, true, null, transaction),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      ...auxillaryData.ExistingClockActivities.map(activity => this.activityService.updateActivity(DataChangeEmitWho.Sync, activity, true, null, transaction)),
    ]);
  }

  async onUpdateAssignmentSuccess(auxillaryData: UpdateAssignmentAuxillaryDto, response: UpdateAssignmentResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
    ]);
  }

  async onUpdateAssignmentFailure(auxillaryData: UpdateAssignmentAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.activityLogService.updateActivityLog(DataChangeEmitWho.Sync, auxillaryData.ExistingActivityLog, true, null, transaction),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      ...auxillaryData.ExistingActivities.map(activity => this.activityService.updateActivity(DataChangeEmitWho.Sync, activity, true, null, transaction)),
      ...auxillaryData.ExistingOdometerReadings.map(odometerReading => this.odometerReadingService.updateOdometerReading(DataChangeEmitWho.Sync, odometerReading, true, null, transaction)),
    ]);
  }

  async onSubmitTimeFailure(auxillaryData: SubmitTimeAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.activityLogService.updateActivityLog(DataChangeEmitWho.Sync, auxillaryData.ExistingRecords as ActivityLog, true, null, transaction),
      ...auxillaryData.ExistingRecords.TimeRecords.map(timeRecord => this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, timeRecord as TimeRecord, true, null, transaction)),
    ]);
  }

  async onVerifyTimeFailure(auxillaryData: VerifyTimeAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.activityLogService.updateActivityLog(DataChangeEmitWho.Sync, auxillaryData.ExistingRecords as ActivityLog, true, null, transaction),
      ...auxillaryData.ExistingRecords.TimeRecords.map(timeRecord => this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, timeRecord as TimeRecord, true, null, transaction)),
    ]);
  }

  async onApproveTimeFailure(auxillaryData: ApproveTimeAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.activityLogService.updateActivityLog(DataChangeEmitWho.Sync, auxillaryData.ExistingRecords as ActivityLog, true, null, transaction),
      ...auxillaryData.ExistingRecords.TimeRecords.map(timeRecord => this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, timeRecord as TimeRecord, true, null, transaction)),
    ]);
  }

  async onReturnTimeFailure(auxillaryData: ReturnTimeAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.activityLogService.updateActivityLog(DataChangeEmitWho.Sync, auxillaryData.ExistingRecords as ActivityLog, true, null, transaction),
      ...auxillaryData.ExistingRecords.TimeRecords.map(timeRecord => this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, timeRecord as TimeRecord, true, null, transaction)),
    ]);
  }

  async onCreateNotableActivitySuccess(auxillaryData: CreateNotableActivityAuxillaryDto, response: CreateNotableActivityResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityRecordId, response.ActivityId, transaction)]);
  }

  async onCreateNotableActivityFailure(auxillaryData: CreateNotableActivityAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ActivityRecordId, true, null, transaction)]);
  }

  async onCreateFlexDocActivitySuccess(auxillaryData: CreateFlexDocActivityAuxillaryDto, response: CreateFlexDocActivityResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.updateActivityLogWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, response.ActivityLogId, transaction) : null,
      this.activityService.updateActivityWithId(DataChangeEmitWho.Sync, auxillaryData.ActivityRecordId, response.ActivityId, transaction),
    ]);
  }

  async onCreateFlexDocActivityFailure(auxillaryData: CreateFlexDocActivityAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      auxillaryData.ActivityLogIsNew ? this.activityLogService.hardDeleteActivityLog(DataChangeEmitWho.Sync, auxillaryData.ActivityLogRecordId, true, null, transaction) : null,
      this.activityService.hardDeleteActivity(DataChangeEmitWho.Sync, auxillaryData.ActivityRecordId, true, null, transaction),
    ]);
  }

  async onUpdateNotableActivityFailure(auxillaryData: UpdateNotableActivityAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([this.activityService.updateActivity(DataChangeEmitWho.Sync, auxillaryData.ExistingActivity, true, null, transaction)]);
  }

  async onCreateOdometerReadingDoBefore(action: SavedSyncAction<SyncActionTypeId.CreateOdometerReading>) {
    // Upload media if odometer reading media exists
    if (action.auxillaryData.StartOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.StartOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerStartPhotoBlobName = fileName;
        
        // Update the odometer reading record with the start blob name
        if (action.auxillaryData.OdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.OdometerReadingRecordId, 'StartPhotoBlobName', fileName, transaction);
        }
      }
    }
    if (action.auxillaryData.EndOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.EndOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerEndPhotoBlobName = fileName;
        
        // Update the odometer reading record with the end blob name
        if (action.auxillaryData.OdometerReadingRecordId) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.OdometerReadingRecordId, 'EndPhotoBlobName', fileName, transaction);
        }
      }
    }
  }

  async onCreateOdometerReadingSuccess(auxillaryData: CreateOdometerReadingAuxillaryDto, response: CreateOdometerReadingResponseDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.odometerReadingService.updateOdometerReadingWithId(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, response.OdometerReadingId, transaction),
    ]);
  }

  async onCreateOdometerReadingFailure(auxillaryData: CreateOdometerReadingAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([this.odometerReadingService.hardDeleteOdometerReading(DataChangeEmitWho.Sync, auxillaryData.OdometerReadingRecordId, true, null, transaction)]);
  }

  async onUpdateOdometerReadingDoBefore(action: SavedSyncAction<SyncActionTypeId.UpdateOdometerReading>) {
    if (!action.params.odometerReadingId) {
      const odometerReading = await this.odometerReadingService.getOdometerReadingByRecordId(action.auxillaryData.ExistingOdometerReading.RecordId, null, true);
      if (!odometerReading?.Id) {
        throw new DependantSyncActionException(odometerReading);
      }
      action.params.odometerReadingId = odometerReading.Id;
    }
    if (action.auxillaryData.StartOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.StartOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerStartPhotoBlobName = fileName;
        
        // Update the odometer reading record with the start blob name
        if (action.auxillaryData.ExistingOdometerReading) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.ExistingOdometerReading.RecordId, 'StartPhotoBlobName', fileName, transaction);
        }
      }
    } else if (action.auxillaryData.ExistingOdometerReading?.StartPhotoBlobName) {
      // Use existing filename if no new media was created
      action.payload.OdometerStartPhotoBlobName = action.auxillaryData.ExistingOdometerReading.StartPhotoBlobName;
    }
    if (action.auxillaryData.EndOdometerReadingMediaRecordId) {
      const fileName = await this.odometerReadingMediaService.uploadOdometerReadingMediaIfNeeded(action.auxillaryData.EndOdometerReadingMediaRecordId);
      if (fileName) {
        action.payload.OdometerEndPhotoBlobName = fileName;
        
        // Update the odometer reading record with the end blob name
        if (action.auxillaryData.ExistingOdometerReading) {
          const transaction = await this.startTransactionIfNeeded();
          await this.odometerReadingService.updateOdometerReadingProperty(action.auxillaryData.ExistingOdometerReading.RecordId, 'EndPhotoBlobName', fileName, transaction);
        }
      }
    } else if (action.auxillaryData.ExistingOdometerReading?.EndPhotoBlobName) {
      // Use existing filename if no new media was created
      action.payload.OdometerEndPhotoBlobName = action.auxillaryData.ExistingOdometerReading.EndPhotoBlobName;
    }
  }

  async onUpdateOdometerReadingFailure(auxillaryData: UpdateOdometerReadingAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([this.odometerReadingService.updateOdometerReading(DataChangeEmitWho.Sync, auxillaryData.ExistingOdometerReading, true, null, transaction)]);
  }

  async onDeleteTimeFailure(auxillaryData: DeleteTimeAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([
      this.activityLogService.updateActivityLog(DataChangeEmitWho.Sync, auxillaryData.ExistingActivityLog, true, null, transaction),
      this.timeRecordService.updateTimeRecord(DataChangeEmitWho.Sync, auxillaryData.ExistingTimeRecord, true, null, transaction),
      ...auxillaryData.ExistingActivities.map(activity => this.activityService.updateActivity(DataChangeEmitWho.Sync, activity, true, null, transaction)),
    ]);
  }

  async onDeleteNotableActivityFailure(auxillaryData: DeleteNotableActivityAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([this.activityService.updateActivity(DataChangeEmitWho.Sync, auxillaryData.ExistingActivity, true, null, transaction)]);
  }

  async onDeleteOdometerReadingFailure(auxillaryData: DeleteOdometerReadingAuxillaryDto) {
    const transaction = await this.startTransactionIfNeeded();
    await Promise.all([this.odometerReadingService.updateOdometerReading(DataChangeEmitWho.Sync, auxillaryData.ExistingOdometerReading, true, null, transaction)]);
  }
}
